/**
 * GeoLines — a layer of geographic polylines drawn on the globe surface.
 *
 * Used for both submarine cables and coastline / border outlines. All routes
 * are merged into a single `LineSegments` geometry (one draw call) laid just
 * above the sphere. Each segment is subdivided along the great circle so even
 * sparse, long spans hug the surface instead of cutting a straight chord
 * through the globe. Additive blending makes overlapping lines glow.
 *
 * See pipeline/cables.py, pipeline/coastlines.py, and docs/TELEMETRY_CATALOG.md.
 */

import { useMemo } from 'react';
import {
  AdditiveBlending,
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  LineBasicMaterial,
  NormalBlending,
  Vector3,
} from 'three';

import { latLonToVec3 } from './coords.js';

/** A geographic polyline: an ordered list of `[lon, lat]` points (GeoJSON order). */
export type GeoLine = ReadonlyArray<readonly [number, number]>;

export interface GeoLinesProps {
  readonly lines: ReadonlyArray<GeoLine>;
  /** Radius to lay the lines on. Slightly above the globe surface. Default 1.002. */
  readonly radius?: number;
  readonly color?: string | number;
  readonly opacity?: number;
  /** Additive blending for a glow (default), or normal blending for crisp outlines. */
  readonly additive?: boolean;
  /** Transparent-sort order; lower draws first (further back). Default 0. */
  readonly renderOrder?: number;
}

// Subdivide any segment whose endpoints are farther apart than this (radians),
// so long lines curve along the surface. ~0.06 rad ≈ 3.4°.
const MAX_STEP_RAD = 0.06;

/** Spherical-linear interpolation between two unit vectors. */
function slerpUnit(a: Vector3, b: Vector3, t: number, out: Vector3): Vector3 {
  const dot = Math.min(1, Math.max(-1, a.dot(b)));
  const omega = Math.acos(dot);
  if (omega < 1e-6) return out.copy(a);
  const sinOmega = Math.sin(omega);
  const wa = Math.sin((1 - t) * omega) / sinOmega;
  const wb = Math.sin(t * omega) / sinOmega;
  return out.copy(a).multiplyScalar(wa).addScaledVector(b, wb);
}

export function GeoLines({
  lines,
  radius = 1.002,
  color = 0x2bd6ff,
  opacity = 0.16,
  additive = true,
  renderOrder = 0,
}: GeoLinesProps): JSX.Element | null {
  const geometry = useMemo(() => {
    const positions: number[] = [];
    const dirA = new Vector3();
    const dirB = new Vector3();
    const prev = new Vector3();
    const cur = new Vector3();

    for (const line of lines) {
      for (let i = 0; i + 1 < line.length; i += 1) {
        const a = line[i];
        const b = line[i + 1];
        if (!a || !b) continue;
        dirA.copy(latLonToVec3(a[1], a[0], 1));
        dirB.copy(latLonToVec3(b[1], b[0], 1));
        const angle = dirA.angleTo(dirB);
        const steps = Math.max(1, Math.ceil(angle / MAX_STEP_RAD));
        slerpUnit(dirA, dirB, 0, prev).multiplyScalar(radius);
        for (let s = 1; s <= steps; s += 1) {
          slerpUnit(dirA, dirB, s / steps, cur).multiplyScalar(radius);
          positions.push(prev.x, prev.y, prev.z, cur.x, cur.y, cur.z);
          prev.copy(cur);
        }
      }
    }

    const geo = new BufferGeometry();
    geo.setAttribute('position', new Float32BufferAttribute(positions, 3));
    return geo;
  }, [lines, radius]);

  const material = useMemo(
    () =>
      new LineBasicMaterial({
        color: new Color(color),
        transparent: true,
        opacity,
        blending: additive ? AdditiveBlending : NormalBlending,
        depthWrite: false,
      }),
    [color, opacity, additive],
  );

  if (lines.length === 0) return null;
  return <lineSegments geometry={geometry} material={material} renderOrder={renderOrder} />;
}
